import { addDoc, query, where, getDocs, doc, getDoc, Timestamp, collection } from 'firebase/firestore';
import { db } from '../firebaseFunctions/config';
import { notificationService } from './NotificationService';

export const receiptService = {
  async createReceipt(paymentData) {
    try {
      const platformFeePercent = 0.15;
      const totalAmount = paymentData.totalAmount;
      const platformFee = Math.round(totalAmount * platformFeePercent);
      const eoAmount = totalAmount - platformFee;

      const receiptData = {
        paymentIntentId: paymentData.paymentIntentId,
        transactionId: paymentData.transactionId,
        
        mpsUserId: paymentData.mpsUserId,
        mpsBusinessName: paymentData.mpsBusinessName,
        mpsEmail: paymentData.mpsEmail,
        
        eoUserId: paymentData.eoUserId,
        eoName: paymentData.eoName,
        
        eventId: paymentData.eventId,
        eventName: paymentData.eventName,
        applicationId: paymentData.applicationId,
        
        totalAmount,
        platformFee,
        eoAmount,
        platformFeePercent,
        
        status: 'completed',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const receiptsRef = collection(db, 'receipts');
      const docRef = await addDoc(receiptsRef, receiptData);
      
      notificationService.sendPaymentReceiptEmail({
        mpsEmail: paymentData.mpsEmail,
        mpsBusinessName: paymentData.mpsBusinessName,
        eventName: paymentData.eventName,
        totalAmount,
        platformFee,
        transactionId: paymentData.transactionId,
        receiptId: docRef.id,
        eoUserId: paymentData.eoUserId
      }).catch(err => console.log("Receipt email error:", err));

      // Notify EO that vendor paid
      notificationService.sendVendorPaidNotificationEmail({
        eoEmail: paymentData.eoEmail,
        eoName: paymentData.eoName,
        mpsBusinessName: paymentData.mpsBusinessName,
        eventName: paymentData.eventName,
        amount: totalAmount,
        platformFee: platformFee,
        transactionId: paymentData.transactionId,
      }).catch(err => console.log("EO payment notification error:", err));
      
      return {
        success: true,
        receiptId: docRef.id,
        receipt: { id: docRef.id, ...receiptData }
      };
    } catch (error) {
      console.error('Error creating receipt:', error);
      return { success: false, message: error.message };
    }
  },

  // Get MPS receipts
  async getMPSReceipts(userId) {
    try {
      const receiptsRef = collection(db, 'receipts');
      // Use simple query without orderBy to avoid composite index requirement
      const q = query(
        receiptsRef,
        where('mpsUserId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      const receipts = [];
      querySnapshot.forEach((docSnapshot) => {
        receipts.push({ id: docSnapshot.id, ...docSnapshot.data() });
      });

      // Sort in memory instead of in query
      receipts.sort((a, b) => {
        const dateA = a.createdAt?.seconds || 0;
        const dateB = b.createdAt?.seconds || 0;
        return dateB - dateA;
      });

      return { success: true, receipts };
    } catch (error) {
      console.error('Error fetching MPS receipts:', error);
      if (error.message?.includes('index')) {
        return { success: true, receipts: [], needsIndex: true };
      }
      return { success: false, message: error.message };
    }
  },

  // Get EO receipts
  async getEOReceipts(userId) {
    try {
      const receiptsRef = collection(db, 'receipts');
      // Use simple query without orderBy to avoid composite index requirement
      const q = query(
        receiptsRef,
        where('eoUserId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      const receipts = [];
      querySnapshot.forEach((docSnapshot) => {
        receipts.push({ id: docSnapshot.id, ...docSnapshot.data() });
      });

      // Sort in memory instead of in query
      receipts.sort((a, b) => {
        const dateA = a.createdAt?.seconds || 0;
        const dateB = b.createdAt?.seconds || 0;
        return dateB - dateA;
      });

      return { success: true, receipts };
    } catch (error) {
      console.error('Error fetching EO receipts:', error);
      if (error.message?.includes('index')) {
        return { success: true, receipts: [], needsIndex: true };
      }
      return { success: false, message: error.message };
    }
  },

  // Get event receipts for EO (alias for getEOReceipts with payment data)
  async getEventReceipts(userId) {
    try {
      const paymentsRef = collection(db, 'payments');
      const q = query(
        paymentsRef,
        where('eoUserId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      const receipts = [];
      querySnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        receipts.push({ 
          id: docSnapshot.id, 
          ...data,
          type: 'mps_registration'
        });
      });

      receipts.sort((a, b) => {
        const dateA = a.createdAt?.seconds || 0;
        const dateB = b.createdAt?.seconds || 0;
        return dateB - dateA;
      });

      return { success: true, receipts };
    } catch (error) {
      console.error('Error fetching event receipts:', error);
      if (error.message?.includes('index')) {
        return { success: true, receipts: [], needsIndex: true };
      }
      return { success: false, error: error.message };
    }
  },

  // Get single receipt
  async getReceipt(receiptId) {
    try {
      const receiptRef = doc(db, 'receipts', receiptId);
      const receiptDoc = await getDoc(receiptRef);
      if (receiptDoc.exists()) {
        return { success: true, receipt: { id: receiptDoc.id, ...receiptDoc.data() } };
      }
      return { success: false, message: 'Receipt not found' };
    } catch (error) {
      console.error('Error fetching receipt:', error);
      return { success: false, message: error.message };
    }
  }
}; 